import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface Plata {
  id: string;
  data: string;
  tip: 'factura_intrare' | 'factura_iesire' | 'bon_transfer' | 'alte_plati';
  documentId: string;
  documentNumar: string;
  suma: number;
  metodaPlata: 'cash' | 'transfer' | 'card' | 'cec';
  parohieId?: string;
  status: 'efectuata' | 'in_asteptare' | 'anulata';
  observatii?: string;
}

const Plati: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTip, setFilterTip] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlata, setSelectedPlata] = useState<Plata | null>(null);

  // Mock data pentru plăți
  const [plati] = useState<Plata[]>([
    {
      id: 'p1',
      data: '2024-01-25',
      tip: 'factura_intrare',
      documentId: 'fi2',
      documentNumar: 'FI-002/2024',
      suma: 952,
      metodaPlata: 'transfer',
      parohieId: 'parohia1',
      status: 'efectuata',
      observatii: 'Plată efectuată prin transfer bancar'
    }
  ]);

  const filteredPlati = plati.filter(plata => {
    const matchesSearch = plata.documentNumar.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plata.observatii?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTip = !filterTip || plata.tip === filterTip;
    const matchesStatus = !filterStatus || plata.status === filterStatus;
    
    return matchesSearch && matchesTip && matchesStatus;
  });

  const totalEfectuate = plati
    .filter(p => p.status === 'efectuata')
    .reduce((sum, p) => sum + p.suma, 0);

  const totalInAsteptare = plati
    .filter(p => p.status === 'in_asteptare')
    .reduce((sum, p) => sum + p.suma, 0);

  const totalGeneral = plati.reduce((sum, p) => sum + p.suma, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'efectuata': return 'success';
      case 'in_asteptare': return 'warning';
      case 'anulata': return 'error';
      default: return 'default';
    }
  };

  const getTipLabel = (tip: string) => {
    switch (tip) {
      case 'factura_intrare': return 'Factură intrare';
      case 'factura_iesire': return 'Factură ieșire';
      case 'bon_transfer': return 'Bon transfer';
      case 'alte_plati': return 'Alte plăți';
      default: return tip;
    }
  };

  const handleViewPlata = (plata: Plata) => {
    setSelectedPlata(plata);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlata(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Plăți
        </Typography>
        {hasPermission('gestiune.write') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* Implementare adăugare plată nouă */}}
          >
            Plată nouă
          </Button>
        )}
      </Box>

      {/* Statistici plăți */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ✅
                </Typography>
                <Typography color="textSecondary">
                  Efectuate
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                €{totalEfectuate.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  ⏳
                </Typography>
                <Typography color="textSecondary">
                  În așteptare
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                €{totalInAsteptare.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" sx={{ mr: 1 }}>
                  💰
                </Typography>
                <Typography color="textSecondary">
                  Total general
                </Typography>
              </Box>
              <Typography variant="h5" color="primary.main">
                €{totalGeneral.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtre pentru plăți */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Caută plăți..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tip</InputLabel>
              <Select
                value={filterTip}
                onChange={(e) => setFilterTip(e.target.value)}
                label="Tip"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="factura_intrare">Factură intrare</MenuItem>
                <MenuItem value="factura_iesire">Factură ieșire</MenuItem>
                <MenuItem value="bon_transfer">Bon transfer</MenuItem>
                <MenuItem value="alte_plati">Alte plăți</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="efectuata">Efectuată</MenuItem>
                <MenuItem value="in_asteptare">În așteptare</MenuItem>
                <MenuItem value="anulata">Anulată</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchTerm('');
                setFilterTip('');
                setFilterStatus('');
              }}
            >
              Resetează
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabel plăți */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Tip</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Suma</TableCell>
              <TableCell>Metodă plată</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlati.map((plata) => (
              <TableRow key={plata.id}>
                <TableCell>{plata.data}</TableCell>
                <TableCell>
                  <Chip
                    label={getTipLabel(plata.tip)}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell>{plata.documentNumar}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold">
                    €{plata.suma.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={plata.metodaPlata}
                    color="info"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={plata.status}
                    color={getStatusColor(plata.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewPlata(plata)}
                    color="primary"
                  >
                    <ViewIcon />
                  </IconButton>
                  {hasPermission('gestiune.write') && (
                    <IconButton color="secondary">
                      <EditIcon />
                    </IconButton>
                  )}
                  <IconButton color="default">
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pentru vizualizare plată */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalii plată
        </DialogTitle>
        <DialogContent>
          {selectedPlata && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data
                </Typography>
                <Typography variant="body1">
                  {selectedPlata.data}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tip
                </Typography>
                <Chip
                  label={getTipLabel(selectedPlata.tip)}
                  color="primary"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Document
                </Typography>
                <Typography variant="body1">
                  {selectedPlata.documentNumar}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Suma
                </Typography>
                <Typography variant="h5" color="primary.main">
                  €{selectedPlata.suma.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Metodă plată
                </Typography>
                <Chip
                  label={selectedPlata.metodaPlata}
                  color="info"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedPlata.status}
                  color={getStatusColor(selectedPlata.status) as any}
                />
              </Grid>
              {selectedPlata.observatii && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Observații
                  </Typography>
                  <Typography variant="body1">
                    {selectedPlata.observatii}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Închide</Button>
          <Button variant="contained" startIcon={<DownloadIcon />}>
            Descarcă
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plati;
